import React, { useId, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchableDropdown } from '@/components/ui/searchable-dropdown';
import { findIngredientByName, searchIngredients } from '@/components/procurement/helpers';
import { fetchIngredientVendors } from '@/lib/api/ingredientVendors';
import { INGREDIENT_CATEGORIES, INGREDIENT_CATEGORY_LABELS, type Ingredient, type IngredientCategory, type Vendor } from '@/types/api';
import type { ExistingItemRef, ImportRow } from '@/types/procurementImport';
import { UNIT_DEFINITIONS, canonicalUnit } from '@/types/units';
import { SelectBox } from './SelectBox';
import { describeOnList, findManualRow, guessCategory, liveListRows, quantityNeedsCheck, resolveExistingRows, type ExistingDecision, type ImportRowState } from './confirmPlan';

interface ImportRowEditorProps {
  state: ImportRowState;
  problems: string[];
  /** List rows for the chosen ingredient, excluded ones included */
  existingRows: ExistingItemRef[];
  /** Every row on the week's list, to seed the vendor when another ingredient is picked */
  existingItems?: ExistingItemRef[];
  /** Pick counter per row key, owned by the review so it survives remounts and resets per preview */
  pickSeqs?: Map<string, number>;
  /** Raw names of other rows this one will be combined with at confirm */
  combinedWith: string[];
  /** Active vendors for the Vendor select */
  vendors?: Vendor[];
  onChange: (patch: Partial<ImportRowState>) => void;
}

/** "Mon 18.5 + Tue 35 + Sat 29 lb = 82.5 lb", from the sheet's converted amounts. */
export function formatBreakdown(row: ImportRow): string {
  const unit = row.unit ? ` ${row.unit}` : '';
  const parts = row.sources.map((s) => `${s.day ?? 'Week'} ${s.convertedQuantity ?? 'not counted'}`);
  const total = row.quantity ?? 0;
  return `${parts.join(' + ')}${unit} = ${total}${unit}`;
}

export function ImportRowEditor({ state, problems, existingRows, existingItems = [], combinedWith, vendors = [], pickSeqs: sharedSeqs, onChange }: ImportRowEditorProps) {
  const { row } = state;
  const [ownSeqs] = useState(() => new Map<string, number>());
  const pickSeqs = sharedSeqs ?? ownSeqs;
  const bumpPick = () => {
    const next = (pickSeqs.get(state.key) ?? 0) + 1;
    pickSeqs.set(state.key, next);
    return next;
  };
  const [creating, setCreating] = useState(false);
  const qtyId = useId();
  const unitId = useId();
  const vendorSelectId = useId();
  const decisionId = useId();
  const includeId = useId();
  const [changing, setChanging] = useState(false);

  const resolved = state.ingredientId !== null;
  const isCreating = creating || state.createIngredient !== null;

  const matchBadge = state.createIngredient
    ? <Badge variant="outline">New ingredient</Badge>
    : resolved ? null
    : <Badge variant="destructive">No match</Badge>;

  // vendorId follows the MANUAL row, else the preferred vendor, unless the caterer chose one by hand.
  const pickIngredient = (ingredient: Pick<Ingredient, 'id' | 'name' | 'defaultUnit'>) => {
    const seq = bumpPick();
    setCreating(false);
    setChanging(false);
    const keepVendor = state.vendorTouched;
    const manual = findManualRow(resolveExistingRows(ingredient.id, existingItems));
    const seeded = keepVendor ? {} : { vendorId: manual ? manual.vendorId ?? null : null };
    // A row with no unit takes the picked ingredient's.
    const unit = state.unit === null ? { unit: canonicalUnit(ingredient.defaultUnit) ?? ingredient.defaultUnit } : {};
    onChange({ ingredientId: ingredient.id, ingredientName: ingredient.name, ingredientUnit: ingredient.defaultUnit, createIngredient: null, ...seeded, ...unit });
    if (keepVendor || manual) return;
    fetchIngredientVendors(ingredient.id)
      .then((links) => {
        const preferred = links.find((link) => link.isPreferred);
        // A later pick or a hand-chosen vendor bumps the counter and drops this result.
        if (seq === pickSeqs.get(state.key) && preferred) onChange({ vendorId: preferred.vendorId });
      })
      .catch(() => {});
  };

  const [notice, setNotice] = useState<string | null>(null);

  const startCreate = async (name: string) => {
    const newName = name.trim() || row.rawName;
    const seq = bumpPick();
    setNotice(null);
    // The library may already have this name: pick it instead of creating a duplicate, and say so when it is deactivated.
    const existing = await findIngredientByName(newName).catch(() => null);
    if (seq !== pickSeqs.get(state.key)) return;
    // An active match is simply picked; the row then shows that ingredient's name.
    if (existing && existing.isActive) { pickIngredient(existing); return; }
    if (existing) {
      setNotice(`"${existing.name}" exists but is deactivated. Reactivate it in the Kitchen Library, or pick another ingredient.`);
      return;
    }
    setCreating(true);
    onChange({ ingredientId: null, ingredientName: null, ingredientUnit: null, ...(state.vendorTouched ? {} : { vendorId: null }), createIngredient: { name: newName, category: guessCategory(newName), defaultUnit: state.unit ?? 'count' } });
  };

  const vendorOptions = state.vendorId !== null && !vendors.some((v) => v.id === state.vendorId)
    ? [...vendors, { id: state.vendorId, name: row.preferredVendor?.id === state.vendorId ? row.preferredVendor.name : `Vendor ${state.vendorId}` }]
    : vendors;

  const showSuggestions = !resolved && !isCreating && row.candidates.length > 0;
  const showPicker = !resolved || changing;
  const summary: React.ReactNode[] = [];
  if (row.sources.length > 1) summary.push(<span key="breakdown" data-testid="day-breakdown">{formatBreakdown(row)}</span>);
  if (existingRows.length > 0) {
    const hasRecipe = liveListRows(existingRows).length > 0;
    summary.push(
      <span key="existing" data-testid="on-list">
        {describeOnList(existingRows)}{hasRecipe ? ', ' : ''}
        {hasRecipe && (
          <>
            <label htmlFor={decisionId} className="sr-only">Amount to use</label>
            <SelectBox id={decisionId} variant="text" value={state.existingDecision} onChange={(e) => onChange({ existingDecision: e.target.value as ExistingDecision })}>
              <option value="use-sheet">using the sheet amount</option>
              <option value="keep-recipe">keeping the list amount</option>
              <option value="add-both">adding both</option>
            </SelectBox>
          </>
        )}
      </span>,
    );
  }
  if (combinedWith.length > 0) summary.push(<span key="combined">Will be combined with {combinedWith.join(', ')}</span>);
  if (row.issues.includes('in-kitchen')) summary.push(<span key="kitchen">Marked as in the kitchen — unticked; tick to buy anyway</span>);
  if (row.notes) summary.push(<span key="notes">{row.notes}</span>);

  return (
    <div data-testid="import-row" data-key={state.key} className={`grid grid-cols-1 items-start gap-x-3 gap-y-2 rounded-lg border p-3 md:grid-cols-[auto_minmax(0,1fr)_6rem_7rem_11rem] ${state.included ? '' : 'opacity-60'}`}>
      <label className="flex h-9 items-center" htmlFor={includeId}>
        <input id={includeId} type="checkbox" className="h-4 w-4" checked={state.included} onChange={(e) => onChange({ included: e.target.checked })} />
        <span className="sr-only">Include {row.rawName}</span>
      </label>

      <div className="min-w-0 space-y-1.5">
        <div className="flex min-h-9 flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">{row.rawName}</span>
          {matchBadge}
        </div>

        {isCreating ? (
          <div className="flex flex-wrap items-center gap-2">
            <Input aria-label="New ingredient name" className="w-full min-w-40 flex-1" value={state.createIngredient?.name ?? ''} onChange={(e) => onChange({ createIngredient: { ...(state.createIngredient ?? { category: 'OTHER' as IngredientCategory, defaultUnit: state.unit ?? 'count' }), name: e.target.value } })} />
            <SelectBox aria-label="Category" className="w-36" value={state.createIngredient?.category ?? 'OTHER'}
              onChange={(e) => onChange({ createIngredient: { ...(state.createIngredient ?? { name: row.rawName, defaultUnit: state.unit ?? 'count' }), category: e.target.value as IngredientCategory } })}>
              {INGREDIENT_CATEGORIES.map((c) => <option key={c} value={c}>{INGREDIENT_CATEGORY_LABELS[c]}</option>)}
            </SelectBox>
            <Button type="button" variant="ghost" size="sm" onClick={() => { setCreating(false); onChange({ createIngredient: null }); }}>Cancel</Button>
          </div>
        ) : (
          <>
            {showPicker ? (
              <div className="flex items-center gap-2">
                <div className="min-w-0 max-w-72 flex-1">
                  <SearchableDropdown<Ingredient>
                    placeholder={state.ingredientName ?? 'Search ingredients…'}
                    ariaLabel={`Search ingredients for ${row.rawName}`}
                    onSearch={searchIngredients}
                    onSelect={pickIngredient}
                    renderItem={(ingredient) => (
                      <div className="flex w-full items-center justify-between gap-3">
                        <span>{ingredient.name}</span>
                        <span className="text-xs text-muted-foreground">{ingredient.defaultUnit}</span>
                      </div>
                    )}
                    getItemKey={(ingredient) => ingredient.id}
                    noMatchesContent={(query: string) => (
                      <button type="button" className="w-full px-2 py-1.5 text-left text-sm" onClick={() => startCreate(query)}>
                        Create "{query}"
                      </button>
                    )}
                  />
                </div>
                {resolved && <Button type="button" variant="ghost" size="sm" onClick={() => setChanging(false)}>Cancel</Button>}
              </div>
            ) : (
              <div className="flex min-h-9 items-center gap-2">
                <span className="truncate rounded-md border bg-muted px-2 py-1 text-sm">{state.ingredientName}</span>
                <Button type="button" variant="ghost" size="sm" onClick={() => setChanging(true)}>Change</Button>
              </div>
            )}
            {!resolved && (
              <div className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
                {showSuggestions && (
                  <>
                    <span>Did you mean</span>
                    {row.candidates.slice(0, 2).map((c, i) => (
                      <span key={c.id}>
                        {i > 0 && <span aria-hidden="true"> · </span>}
                        <button type="button" className="text-primary underline-offset-2 hover:underline" onClick={() => pickIngredient(c)}>{c.name}</button>
                      </span>
                    ))}
                  </>
                )}
                <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-xs" title={`Create "${row.rawName}" as a new ingredient`} onClick={() => startCreate(row.rawName)}>Create</Button>
              </div>
            )}
          </>
        )}
      </div>

      <div>
        <label htmlFor={qtyId} className="sr-only">Quantity</label>
        <Input id={qtyId} className="w-full" inputMode="decimal" value={state.quantity} placeholder={row.rawQuantity || 'Quantity'} onChange={(e) => onChange({ quantity: e.target.value, quantityChecked: true })} />
      </div>
      <div>
        <label htmlFor={unitId} className="sr-only">Unit</label>
        <SelectBox id={unitId} title={UNIT_DEFINITIONS.find((u) => u.value === state.unit)?.label} value={state.unit ?? ''} onChange={(e) => {
          const unit = e.target.value || null;
          // A new ingredient is created in whatever unit the row ends up in.
          onChange({ unit, ...(state.createIngredient ? { createIngredient: { ...state.createIngredient, defaultUnit: unit ?? 'count' } } : {}) });
        }}>
          <option value="">Unit…</option>
          {UNIT_DEFINITIONS.map((u) => <option key={u.value} value={u.value} title={u.label}>{u.value}</option>)}
        </SelectBox>
      </div>
      <div>
        <label htmlFor={vendorSelectId} className="sr-only">Vendor</label>
        <SelectBox id={vendorSelectId} disabled={!state.included} value={state.vendorId ?? ''} onChange={(e) => {
          bumpPick();
          onChange({ vendorId: e.target.value === '' ? null : Number(e.target.value), vendorTouched: true });
        }}>
          <option value="">Unassigned</option>
          {vendorOptions.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </SelectBox>
      </div>

      {summary.length > 0 && (
        <div className="col-span-full flex flex-wrap items-center gap-x-1 text-xs text-muted-foreground md:pl-7">
          {summary.map((part, i) => <React.Fragment key={i}>{i > 0 && <span aria-hidden="true"> · </span>}{part}</React.Fragment>)}
        </div>
      )}
      {notice && <p role="status" className="col-span-full text-xs text-muted-foreground md:pl-7">{notice}</p>}
      {problems.length > 0 && (
        <div className="col-span-full flex flex-wrap items-center gap-x-2 gap-y-1 md:pl-7">
          <p className="text-xs text-destructive">{problems.map((p, i) => (i < problems.length - 1 && !p.endsWith('.') ? `${p}.` : p)).join(' ')}</p>
          {quantityNeedsCheck(state) && (
            <Button type="button" variant="outline" size="sm" className="h-6 px-2 text-xs" title="Keep the sheet total as shown" onClick={() => onChange({ quantityChecked: true })}>Looks right</Button>
          )}
        </div>
      )}
    </div>
  );
}
